import { memo, useMemo } from 'react'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'

import { importanceBadgeClass } from '../constants'
import { HourlyLimitBar } from './HourlyLimitBar'
import type { AllocationBoard } from '../types'

// Fixed column widths (the table is `table-fixed`, so these header widths drive
// every column and content wraps within them). Duty is bounded — wide enough for
// most names without dominating — and Hourly limit gets room so its bar can span
// the cell; the short numeric columns take an even share of the rest.
const COL = {
  duty: 'w-[28%]',
  priority: 'w-[12%]',
  hourly: 'w-[24%]',
  numeric: 'w-[12%]',
} as const

/**
 * Backlog table: one row per duty with current demand — pending items, OR a live
 * line held, OR an in-flight claim (a union, so a zero-pending row is expected).
 * Each row is a color dot + duty name
 * + org slug, a priority badge (shown only when priority > 1), the hourly-limit
 * bar (calls this hour vs cap), and the pending split into Waiting (eligible now)
 * and Not in window (not yet eligible), plus lines held (reserved). "Priority" is
 * the UI label for the contract's `importance` field. Includes duties of any
 * status; non-active ones carry a status badge and are lightly dimmed. Rows are
 * ordered by org slug then duty name (a stable order), with inactive duties floated
 * to the bottom. The empty case is handled by the page, so this only renders with
 * rows.
 */
export const AllocationTable = memo(function AllocationTable({
  board,
  colorFor,
}: {
  board: AllocationBoard
  colorFor: (dutyId: string) => string
}) {
  // Stable display order: active duties first, then sorted by org slug and duty
  // name. Those identifiers don't change poll-to-poll, so rows hold their position
  // instead of reshuffling as calls are processed (unlike the backend's
  // reserved/pending order). Inactive duties sink to the bottom, sorted the same
  // way within. Recomputed only when the duties list changes.
  const rows = useMemo(
    () =>
      [...board.duties].toSorted((a, b) => {
        const inactiveDiff =
          Number(a.status !== 'active') - Number(b.status !== 'active')
        if (inactiveDiff !== 0) return inactiveDiff
        const orgDiff = (a.org_slug ?? '').localeCompare(b.org_slug ?? '')
        if (orgDiff !== 0) return orgDiff
        return (a.duty_name ?? '').localeCompare(b.duty_name ?? '')
      }),
    [board.duties]
  )

  return (
    <Table className="table-fixed">
      <TableHeader>
        <TableRow>
          <TableHead className={COL.duty}>Duty</TableHead>
          <TableHead className={COL.priority}>Priority</TableHead>
          <TableHead className={COL.hourly}>Hourly limit</TableHead>
          <TableHead className={cn(COL.numeric, 'text-right')}>
            Waiting
          </TableHead>
          <TableHead className={cn(COL.numeric, 'text-right')}>
            Not in window
          </TableHead>
          <TableHead className={cn(COL.numeric, 'text-right')}>
            Lines held
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((duty) => {
          const isInactive = duty.status !== 'active'
          return (
            <TableRow
              key={duty.duty_id}
              // Inactive duties are still shown (they can carry a backlog). Dimmed
              // only lightly: opacity-50 over an already-muted 11px org slug pushed
              // that text well below 4.5:1, so the rows carrying extra meaning were
              // the hardest to read. The status badge below is the real signal —
              // dimming alone was a visual-only cue with just a title as fallback.
              className={cn(isInactive && 'opacity-80')}>
              <TableCell>
                <div className="flex items-center gap-2.5">
                  <span
                    className="size-2.5 shrink-0 rounded-[3px]"
                    style={{ backgroundColor: colorFor(duty.duty_id) }}
                  />
                  <div>
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium text-foreground">
                        {duty.duty_name ?? 'Unnamed'}
                      </span>
                      {/* Explicit textual status, so "this duty isn't running" is
                          readable and announced rather than inferred from dimming.
                          Any non-active status is shown verbatim — the contract
                          allows more than just 'inactive'. */}
                      {isInactive && (
                        <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                          {duty.status}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      {duty.org_slug ?? '—'}
                    </div>
                  </div>
                </div>
              </TableCell>
              <TableCell>
                {/* Priority badge is noise at the default weight — only surface
                    it when a duty is actually prioritized (> 1). */}
                {duty.importance > 1 && (
                  <span
                    className={cn(
                      'inline-flex min-w-[24px] items-center justify-center rounded-md px-1.5 py-0.5 font-mono text-xs font-semibold',
                      importanceBadgeClass(duty.importance)
                    )}>
                    {duty.importance}
                  </span>
                )}
              </TableCell>
              <TableCell>
                <HourlyLimitBar duty={duty} />
              </TableCell>
              <TableCell className="text-right font-medium tabular-nums">
                {duty.pending_waiting.toLocaleString()}
              </TableCell>
              <TableCell className="text-right tabular-nums text-muted-foreground">
                {duty.pending_not_in_window.toLocaleString()}
              </TableCell>
              <TableCell className="text-right font-medium tabular-nums">
                {duty.reserved.toLocaleString()}
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
})
